import React, { useEffect } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import MailIcon from "assets/icons/mail.svg";
import LockIcon from "assets/icons/lock.svg";
import { useAuthStore } from "stores/auth/authStore";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { PasswordInput } from "components/ui/password-input";
import { Loader2 } from "lucide-react";
import ConfirmSignUpForm from "../ConfirmSignUpForm";
import { confirmSignUp, resendSignUpCode } from "@aws-amplify/auth";
import ModalStore from "components/Modal/ModalStore";
import { GET_USER_DATA } from "apollo/queries/ClientProfile";
import { useLazyQuery } from "@apollo/client";
import { Modal, useModalStore } from "stores/modal/useModalStore";

const schema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" }),
  password: z.string().min(1, { message: "Password is required" }),
});
const schemaConfirmSignUp = z.object({
  prompt_code: z.string().min(1, { message: "Code is required" }),
});

const LoginForm = () => {
  const open = useModalStore((s) => s.open);
  const close = useModalStore((s) => s.close);
  const login = useAuthStore((state) => state.login);
  const error = useAuthStore((state) => state.error);
  const isAction = useAuthStore((state) => state.isAction);
  const setIsAction = useAuthStore((state) => state.setIsAction);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [isConfirmSignUpShow, setIsConfirmSignUpShow] = useState(null);
  const [email, setEmail] = useState("");
  const [countdown, setCountdown] = useState(120);
  const [showCountDown, setShowCountDown] = useState(true);

  const [fetchUserProfile] = useLazyQuery(GET_USER_DATA);

  const currentSchema =
    isConfirmSignUpShow === null ? schema : schemaConfirmSignUp;
  const form = useForm({
    mode: "onBlur",
    resolver: zodResolver(currentSchema),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const onHandleForgotPasswordClick = (event) => {
    event.preventDefault();
    close();
    open(Modal.ForgotPassword);
  };

  const onHandleSignUpClick = (event) => {
    event.preventDefault();
    close();
    open(Modal.Register);
  };

  const handleResendCode = async () => {
    try {
      await resendSignUpCode({ username: email });
      setShowCountDown(true);
      setCountdown(120);
    } catch (error) {
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false,
      });
    }
  };
  useEffect(() => {
    if (countdown === 0) {
      setShowCountDown(false);
    }
  }, [countdown]);

  useEffect(() => {
    let interval;
    if (isConfirmSignUpShow) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) =>
          prevCountdown > 0 ? prevCountdown - 1 : 0
        );
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isConfirmSignUpShow]);

  const onHandleSignIn = async (data) => {
    console.log("data", data);
    try {
      setLoading(true);
      if (isConfirmSignUpShow) {
        const response = await confirmSignUp({
          username: email,
          confirmationCode: data?.prompt_code,
        });
        console.log("response", response);
        if (response?.isSignUpComplete) {
          ModalStore.openSimpleSuccess({
            title: "SUCCESS!",
            subtitle: "Registration completed now you can login",
            shouldCloseOnOverlayClick: false,
          });
          setIsConfirmSignUpShow(null);
        }
      }
      const result = await login({
        email: data?.email,
        password: data?.password,
      });
      console.log("result", result);
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        setEmail(data?.email);
        const resendResponse = await resendSignUpCode({
          username: data?.email,
        });
        console.log("resendResponse", resendResponse);
        setIsConfirmSignUpShow(true);
      }

      if (result.success) {
        close();
        // navigate("/");
        const { data: profileData } = await fetchUserProfile();
        console.log("profileData", profileData);
        if (isAction === "selectBuyProduct") {
          navigate("/buy");
        } else if (isAction === "saddleDetails") {
          // navigate(location.pathname);
          navigate("/buy");
        } else if (isAction === "BookEvent") {
          setIsAction("login");
        } else {
          setIsAction("login");
        }

        // Check if user needs to complete profile (skip while completing an event booking)
        if (
          isAction !== "BookEvent" &&
          !profileData?.user?.address &&
          !profileData?.user?.city &&
          !profileData?.user?.state &&
          !profileData?.user?.zipCode
        ) {
          navigate("/profile");
          setTimeout(() => {
            ModalStore.closeAll();
            ModalStore.openSimpleSuccess({
              fontSize: "40px",
              subTitleFont: "14px",
              title: "Almost There!",
              subtitle:
                "Just one more step—fill in your address to keep going.",
              shouldCloseOnOverlayClick: false,
            });
            open(Modal.Profile);
          }, 1000);
        }
        // Klaviyo tracking
        // if (typeof window !== 'undefined' && window.klaviyo) {
        //   window.klaviyo.push([
        //     "identify",
        //     {
        //       email: profileData?.user?.email,
        //       $first_name: profileData?.user?.firstName,
        //       $last_name: profileData?.user?.lastName,
        //     },
        //   ]);
        //   window.klaviyo.push(["track", "Active on Site"]);
        // }
        // Handle navigation based on action
      }

      // closeModal("isLoginModalOpen");
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    return (
      <div className="register-modal-content">
        <div className="register-content">
          <div className="register-content-form">
            <div>
              <Input
                className="register-content-form__field"
                name="email"
                label="Email"
                icon={
                  <img
                    src={MailIcon}
                    alt="email"
                    className="w-[15px] h-[15px]"
                  />
                }
                placeholder={"example@example.com"}
                {...register("email")}
              />
              <div className="h-2 ml-2">
                {errors.email && (
                  <p className="text-red-500 text-sm font-montserrat mt-1">
                    {errors.email?.message}
                  </p>
                )}
              </div>
            </div>
            <div className="">
              <PasswordInput
                className="register-content-form__field"
                name="password"
                label="Password"
                icon={
                  <img
                    src={LockIcon}
                    alt="password"
                    className="w-[15px] h-[15px]"
                  />
                }
                placeholder={"Enter password"}
                {...register("password")}
              />
              <div className="h-2 ml-2">
                {errors.password && (
                  <p className="text-red-500 text-sm font-montserrat mt-1">
                    {errors.password?.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="register-content-submit mt-[22px]">
            {error && (
              <p className="text-red-500 text-sm font-montserrat mt-1">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className={"modal_form_btn"}
              variant="default"
              size="lg"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
            </Button>
            <span className={"register-content-submit__text"}>
              {"Don't have an account? "}
              <Link
                to={"#"}
                onClick={onHandleSignUpClick}
                className={"font-bold underline text-primary"}
              >
                Sign up here
              </Link>
            </span>
            <span className={"register-content-submit__text"}>
              Forgot password?{" "}
              <Link
                to={"#"}
                onClick={onHandleForgotPasswordClick}
                className={"font-bold underline text-primary"}
              >
                Click here
              </Link>
            </span>
          </div>

          <div className="register-content-links">
            <Link
              onClick={() => close()}
              to="/terms-and-conditions"
              className={"text-secondary"}
            >
              Terms of service
            </Link>
            <Link
              onClick={() => close()}
              to="/privacy-policy"
              className={"text-secondary"}
            >
              Privacy policy
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmSignUpForm = () => {
    return (
      <ConfirmSignUpForm
        loading={loading}
        register={register}
        errors={errors}
        onResend={handleResendCode}
        disabled={loading}
        countdown={countdown}
        showCountDown={showCountDown}
      />
    );
  };
  return (
    <>
      <FormProvider {...form} key={isConfirmSignUpShow ? "confirm" : "login"}>
        <form onSubmit={handleSubmit(onHandleSignIn)}>
          {isConfirmSignUpShow === null
            ? renderForm()
            : renderConfirmSignUpForm()}
        </form>
      </FormProvider>
    </>
  );
};

export default LoginForm;

{
  /* Social login buttons temporarily disabled
          <div className="social_buttons">
            <Button
              whiteOutline
              primary
     A         transparent
              AppendIcon={<FbIcon className="mt-[-5px]" />}
            >
              Continue with Facebook
            </Button>
            <Button
              primary
              whiteOutline
              transparent
              AppendIcon={<GoogleIcon className="mt-[-5px]" />}
              onClick={(e) => {
                e.preventDefault();
                signInWithRedirect({
                  provider: "Google",
                  customState: "shopping-cart",
                });
              }}
            >
              Continue with Google
            </Button>
            <Button
              primary
              transparent
              AppendIcon={<MicrosoftIcon className=" mt-[-5px]" />}
            >
              Continue with Microsoft
            </Button>
          </div>
          */
}
